import React, { useState } from 'react';
import { Loader2, Pencil, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatEUR, mensajeError } from '../../lib/api';
import { useMarcasPaciente } from '../../lib/marcasPaciente';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { IndicadorMarcaPaciente } from '../patient/BotonesMarcasPaciente';
import { EtiquetaMarcaPaciente } from '../citas/EtiquetasMarcasPaciente';

/**
 * Configuración → «Marcas de paciente» (gestión; administración, 14/10/2026).
 *
 * Cada marca es un botón en la ficha del paciente («+ <nombre>») que, puesto,
 * enseña su etiqueta de color junto al nombre en la ficha (con el nombre) y en
 * cada cita que se le dé, en la agenda, el resumen y el parte (con el `texto
 * en las citas`; administración, 21/10/2026: antes era una línea en las notas
 * de administración) y, si lleva recargo, lo suma al precio de la
 * cirugía en sus presupuestos de cirugía nuevos (sin línea aparte: el
 * paciente no lo ve). Nada de esto está en el código: sin ninguna marca no hay
 * botón. El servidor apunta cada cambio en Control rastreo.
 *
 * El color nunca es rojo: el rojo es de «PROHIBIDO» (el servidor lo rechaza).
 *
 * «La reserva de quirófano se paga fuera» (administración, 30/10/2026): a los
 * pacientes con esa marca, la confirmación de cirugía por WhatsApp no les
 * menciona el pago de la reserva ni les manda el enlace (la pagan en la
 * consulta de su médico). Por defecto, desmarcada.
 */
const COLORES = [
  { valor: '#7C3AED', nombre: 'Violeta' },
  { valor: '#2563EB', nombre: 'Azul' },
  { valor: '#0F766E', nombre: 'Verde azulado' },
  { valor: '#B45309', nombre: 'Ámbar' },
  { valor: '#0369A1', nombre: 'Azul oscuro' },
  { valor: '#4B5563', nombre: 'Gris' },
  // Pedido por dirección (16/10/2026). Un rosa palo oscurecido: el texto del
  // indicador va en blanco encima y con el claro no se leía (contraste 4,6).
  // El servidor no lo toma por el rojo de PROHIBIDO.
  { valor: '#A8606F', nombre: 'Rosa palo' },
];
const VACIA = { nombre: '', texto_en_citas: '', recargo_cirugia: '0', color: COLORES[0].valor, activa: true, reserva_fuera: false };
const ROTULO_RESERVA_FUERA = 'La reserva de quirófano se paga fuera: la confirmación de cirugía no menciona el pago de la reserva ni lleva el enlace';

function Formulario({ inicial, onGuardar, onCancelar, guardando, testId }) {
  const [f, setF] = useState(inicial);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="rounded-md border border-border p-3 space-y-3" data-testid={testId}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${testId}-nombre`}>Nombre (botón y etiqueta de la ficha)</Label>
          <Input id={`${testId}-nombre`} value={f.nombre} maxLength={60}
            onChange={(e) => set('nombre', e.target.value)} data-testid={`${testId}-nombre`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-texto`}>Texto en las citas (su etiqueta)</Label>
          <Input id={`${testId}-texto`} value={f.texto_en_citas} maxLength={120}
            placeholder="Si va vacío, el nombre"
            onChange={(e) => set('texto_en_citas', e.target.value)} data-testid={`${testId}-texto`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-recargo`}>Recargo en presupuestos de cirugía (€)</Label>
          <Input id={`${testId}-recargo`} type="number" min={0} step="0.01" value={f.recargo_cirugia}
            onChange={(e) => set('recargo_cirugia', e.target.value)} data-testid={`${testId}-recargo`} />
          <div className="text-[11px] text-muted-foreground mt-1">
            Se suma al precio de la cirugía al crear el presupuesto. 0 = sin recargo.
          </div>
        </div>
        <div>
          <Label>Color</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5" role="radiogroup" aria-label="Color de la marca">
            {COLORES.map((c) => (
              <button
                key={c.valor}
                type="button"
                role="radio"
                aria-checked={f.color === c.valor}
                title={c.nombre}
                onClick={() => set('color', c.valor)}
                className={`w-7 h-7 rounded-full border-2 ${f.color === c.valor ? 'border-[#13231E] ring-2 ring-offset-1 ring-[#13231E]/30' : 'border-white'}`}
                style={{ backgroundColor: c.valor }}
                data-testid={`${testId}-color-${c.valor.slice(1)}`}
              />
            ))}
          </div>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!f.activa} onChange={(e) => set('activa', e.target.checked)}
          data-testid={`${testId}-activa`} />
        Activa (sale el botón en la ficha)
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={!!f.reserva_fuera}
          onChange={(e) => set('reserva_fuera', e.target.checked)}
          data-testid={`${testId}-reserva-fuera`} />
        {ROTULO_RESERVA_FUERA}
      </label>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>Así se verá:</span>
          <IndicadorMarcaPaciente marca={{ nombre: f.nombre || 'Nombre', color: f.color }} />
          <span>en la ficha ·</span>
          <EtiquetaMarcaPaciente texto={(f.texto_en_citas || '').trim() || f.nombre || 'Nombre'} color={f.color}
            testId={`${testId}-vista-cita`} />
          <span>en sus citas</span>
        </div>
        <div className="flex gap-2">
          {onCancelar && <Button type="button" variant="outline" size="sm" onClick={onCancelar}>Cancelar</Button>}
          <Button type="button" size="sm" disabled={guardando || !f.nombre.trim()}
            onClick={() => onGuardar(f)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={`${testId}-guardar`}>
            {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MarcasPacientePanel() {
  const [recarga, setRecarga] = useState(0);
  const marcas = useMarcasPaciente({ todas: true, recarga });
  const [editando, setEditando] = useState(null); // 'nueva' | id
  const [guardando, setGuardando] = useState(false);

  const guardar = async (f, id = null) => {
    setGuardando(true);
    const cuerpo = {
      nombre: f.nombre.trim(),
      texto_en_citas: (f.texto_en_citas || '').trim(),
      recargo_cirugia: f.recargo_cirugia === '' ? 0 : Number(f.recargo_cirugia),
      color: f.color,
      activa: !!f.activa,
      reserva_fuera: !!f.reserva_fuera,
    };
    try {
      if (id) await api.put(`/api/marcas-paciente/${id}`, cuerpo);
      else await api.post('/api/marcas-paciente', cuerpo);
      toast.success(id ? 'Marca guardada' : 'Marca creada');
      setEditando(null);
      setRecarga((x) => x + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar la marca'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <SectionCard
      title="Marcas de paciente"
      className="lg:col-span-2"
      action={editando !== 'nueva' && (
        <Button type="button" size="sm" variant="outline" onClick={() => setEditando('nueva')} data-testid="marcas-paciente-nueva">
          <Plus className="w-4 h-4 mr-1" /> Nueva marca
        </Button>
      )}
    >
      <div data-testid="marcas-paciente-panel" className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Cada marca es un botón en la ficha del paciente. Puesta, sale su etiqueta de color junto al
          nombre, en la ficha y en sus citas, y, si lleva recargo, se suma al precio de la cirugía en sus
          presupuestos de cirugía nuevos (el paciente no lo ve aparte). Si su reserva de quirófano se
          paga fuera, su confirmación de cirugía sale sin el pago de la reserva ni el enlace.
        </p>
        {editando === 'nueva' && (
          <Formulario inicial={VACIA} guardando={guardando} testId="marca-paciente-form-nueva"
            onGuardar={(f) => guardar(f)} onCancelar={() => setEditando(null)} />
        )}
        {marcas.length === 0 && editando !== 'nueva' ? (
          <div className="text-sm text-muted-foreground" data-testid="marcas-paciente-vacio">
            No hay ninguna marca: en la ficha del paciente no sale ningún botón.
          </div>
        ) : (
          <ul className="space-y-2">
            {marcas.map((m) => (
              <li key={m.id} data-testid={`marca-paciente-fila-${m.id}`}>
                {editando === m.id ? (
                  <Formulario
                    inicial={{ ...VACIA, ...m, recargo_cirugia: String(m.recargo_cirugia ?? 0) }}
                    guardando={guardando}
                    testId={`marca-paciente-form-${m.id}`}
                    onGuardar={(f) => guardar(f, m.id)}
                    onCancelar={() => setEditando(null)}
                  />
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2 flex-wrap text-sm">
                      <IndicadorMarcaPaciente marca={{ ...m, activa: true }} />
                      <span className="text-muted-foreground">en las citas:</span>
                      <EtiquetaMarcaPaciente texto={m.texto_en_citas || m.nombre} color={m.color} />
                      <span className="text-xs text-muted-foreground">
                        · {Number(m.recargo_cirugia) > 0 ? `recargo ${formatEUR(m.recargo_cirugia)}` : 'sin recargo'}
                      </span>
                      {m.reserva_fuera && (
                        <span className="text-xs text-muted-foreground" data-testid={`marca-paciente-reserva-fuera-${m.id}`}>
                          · reserva de quirófano pagada fuera
                        </span>
                      )}
                      {!m.activa && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-[1px] bg-muted text-muted-foreground">
                          Desactivada
                        </span>
                      )}
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditando(m.id)}
                      data-testid={`marca-paciente-editar-${m.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
